import _ from "lodash";
import React from "react";
import { NavigateFunction, generatePath } from "react-router-dom";
import AuthService from "../../service/authService";
import {
  ApiErrorResponse,
  ApiSuccessResponse,
  COUNTRY,
  Country,
  Currency,
  UserOnboardingInfo,
} from "../../types";
import { ROUTES } from "../../constant/common.constant";
import { identifyUser, trackSignIn } from "../../utils/analytics.utils";
import { TFunction } from "i18next";

const USER_API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

interface CheckEmailDeps {
  setErrorMsg: (msg: string) => void;
  setOnboardingInfo: React.Dispatch<React.SetStateAction<UserOnboardingInfo>>;
  setIsAuthorizedDistributor: (value: boolean) => void;
  onGoToLogin: () => void;
  onGoToSignup: () => void;
}

export const createCheckEmailHandler =
  ({
    setErrorMsg,
    setOnboardingInfo,
    setIsAuthorizedDistributor,
    onGoToLogin,
    onGoToSignup,
  }: CheckEmailDeps) =>
  async (email: string) => {
    setErrorMsg("");
    setIsAuthorizedDistributor(false);
    setOnboardingInfo((prev) => ({ ...prev, email }));
    try {
      await AuthService.userExists(email);
      onGoToLogin();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error?.message;
      const statusCode = error?.response?.status;
      if (errorMessage === "AUTHORIZED_DISTRIBUTOR") {
        setIsAuthorizedDistributor(true);
      }
      if (errorMessage === "AUTHORIZED_SOUVENIR") {
        // TODO: Support when we accept souvenir
      }
      if (statusCode === 429) {
        setErrorMsg("onboard.too_many_requests");
        return;
      }
      onGoToSignup();
    }
  };

export const validatePasswordStep = async (
  password: string,
  t: TFunction,
  setErrorMsg: (msg: string) => void
): Promise<boolean> => {
  try {
    await AuthService.validatePassword(password);
    return true;
  } catch (error: any) {
    const message = error?.response?.data?.message;
    setErrorMsg(t(message ? "sign_up." + message : "sign_up.error"));
    return false;
  }
};

interface SignInDeps {
  inviteToken: string | undefined;
  setErrorMsg: (msg: string) => void;
  initUserInfo: () => void;
  refetchTeam: () => void;
  navigate: NavigateFunction;
  redirectUrl?: string | null;
  getRedirectUrl?: () => string | null;
  onNotDistributor?: (userId: string) => void;
  onDistributorDenied?: () => void;
}

function isLocalhost(hostname: string) {
  const normalizedHostname = hostname.toLowerCase();
  return (
    normalizedHostname === "localhost" || normalizedHostname === "127.0.0.1"
  );
}

const studioHostnameOriginMap: Record<string, string> = {
  "studio.attraction.com": "https://attraction.com",
  "studio.dev.attraction.com": "https://dev.attraction.com",
  "studio.staging.attraction.com": "https://staging.attraction.com",
};
const localhostOriginRule = "localhost";

function mapHostnameToOrigin(hostname: string) {
  const normalizedHostname = hostname.toLowerCase();

  if (isLocalhost(normalizedHostname)) {
    return localhostOriginRule;
  }

  return studioHostnameOriginMap[normalizedHostname] ?? null;
}

function parseAbsoluteUrl(value: string, label: string) {
  if (!URL.canParse(value)) {
    throw new Error(`${label} must be an absolute URL`);
  }

  return new URL(value);
}

export function getSafeAttractionRedirectUrl(
  redirectUrl: string | null,
  studioOrigin = window.location.origin
) {
  if (!redirectUrl) {
    return null;
  }

  if (!URL.canParse(redirectUrl)) {
    return null;
  }

  const studioUrl = parseAbsoluteUrl(studioOrigin, "studioOrigin");
  const parsedRedirectUrl = parseAbsoluteUrl(redirectUrl, "redirectUrl");
  if (!["http:", "https:"].includes(parsedRedirectUrl.protocol)) {
    return null;
  }

  const allowedOrigin = mapHostnameToOrigin(studioUrl.hostname);
  if (!allowedOrigin) {
    return null;
  }

  if (allowedOrigin === localhostOriginRule) {
    return isLocalhost(parsedRedirectUrl.hostname)
      ? parsedRedirectUrl.toString()
      : null;
  }

  if (parsedRedirectUrl.origin !== allowedOrigin) {
    return null;
  }

  return parsedRedirectUrl.toString();
}

export async function hasActiveStudioSession() {
  if (!USER_API_BASE_URL) {
    throw new Error("REACT_APP_BACKEND_URL is required");
  }

  const response = await fetch(`${USER_API_BASE_URL}/api/v1/user/session`, {
    credentials: "include",
  });

  if (response.status === 401 || response.status === 403) {
    return false;
  }

  if (!response.ok) {
    throw new Error(`Unable to check Studio session: ${response.status}`);
  }

  const body = (await response.json()) as unknown;
  return isStudioSessionResponse(body);
}

function isStudioSessionResponse(body: unknown) {
  if (!body || typeof body !== "object" || !("data" in body)) {
    return false;
  }

  const data = body.data;
  if (!data || typeof data !== "object") {
    return false;
  }

  const user = data as Record<string, unknown>;
  return typeof user.id === "string" && typeof user.email === "string";
}

export const createSignInHandler =
  ({
    inviteToken,
    setErrorMsg,
    initUserInfo,
    refetchTeam,
    navigate,
    redirectUrl,
    getRedirectUrl,
    onNotDistributor,
    onDistributorDenied,
  }: SignInDeps) =>
  async (email: string, password: string) => {
    try {
      type LoginSuccess = ApiSuccessResponse<{
        user: {
          country: Country | null;
          currency: Currency;
          email: string;
          id: string;
        };
        joinedTeam: { id: string };
      }>;
      type LoginResult = {
        status: number;
        data: LoginSuccess | ApiErrorResponse | null;
      };

      const res: LoginResult = await AuthService.login({
        email,
        password,
        inviteToken: _.isEmpty(inviteToken) ? null : inviteToken,
      }).catch((error): LoginResult => {
        const statusCode: number = error?.response?.status ?? 0;
        const errorMessage: string = error?.response?.data?.error?.message;
        if (statusCode === 403) {
          if (
            errorMessage === "attraction_not_distributor" &&
            onNotDistributor
          ) {
            const userId = error?.response?.data?.error?.details?.userId ?? "";
            onNotDistributor(userId);
            return { status: 403, data: null };
          }
          if (
            errorMessage === "attraction_distributor_denied" &&
            onDistributorDenied
          ) {
            onDistributorDenied();
            return { status: 403, data: null };
          }
        }
        const displayMessage =
          statusCode === 429 ? "too_many_requests" : errorMessage;
        setErrorMsg("sign_in." + displayMessage);
        return { status: statusCode, data: error.response?.data ?? null };
      });

      if (!res.data || res.status !== 200) return;

      const successData = (res.data as LoginSuccess).data;
      const { user, joinedTeam } = successData;

      AuthService.setLocalUser(user.currency, user.country ?? COUNTRY.CA);

      const userInfo = await AuthService.getUserInfo();

      initUserInfo();
      trackSignIn();

      identifyUser(user.id.toString(), {
        name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        firstName: userInfo?.firstName ?? "",
        lastName: userInfo?.lastName ?? "",
        email: userInfo?.email ?? "",
        role: userInfo?.roles[0].role ?? "",
        forWho: userInfo?.forWho ?? "",
      });

      if (!inviteToken) {
        const safeRedirectUrl = getRedirectUrl?.() ?? redirectUrl;
        if (safeRedirectUrl) {
          window.location.assign(safeRedirectUrl);
          return;
        }
        navigate(ROUTES.HOME);
        return;
      }

      refetchTeam();
      navigate(generatePath(ROUTES.TEAM, { teamId: joinedTeam.id }));
    } catch (error) {
      console.error(error);
    }
  };
